import React, { useEffect, useRef, useState } from "react";
import get from "lodash/get";
import find from "lodash/find";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import RemoveIcon from "@material-ui/icons/Remove";
import IconButton from "@material-ui/core/IconButton";
import FieldOptionsDialog from "./FieldOptionsDialog";
import LayerOptionsDialog from "./LayerOptionsDialog";
import AlertDialog from "./AlertDialog";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@material-ui/icons/Add";
import { removeField, updateField, updateLayer, updateOverallWidth } from "../reducers/Artus/Actions";
import { baseUrl } from "../core/Api";
import arrowUp from "../assets/arrowUp.svg";
import arrowDown from "../assets/arrowDown.svg";
import isNaN from "lodash/isNaN";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: "grid",
    gridTemplateColumns: "auto",
    borderTop: 0,
  },
  flexCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    "&.spacing": {
      marginLeft: ({ scaleFactor }) => -120 / scaleFactor,
    },
  },
  iconButton: {
    backgroundColor: "#c5da86",
    borderWidth: 1,
    borderColor: "#c5da86",
    boxShadow: "0px 2px 4px #00000080",
    borderRadius: 2,
    bottom: "-20px",
    position: "absolute",
    padding: 4,
    "&:hover": {
      backgroundColor: "#c5da86",
    },
  },
  container: {
    position: "relative",
  },
  deleteButton: {
    backgroundColor: "#788184",
    borderWidth: 1,
    borderColor: "#788184",
    boxShadow: "0px 2px 4px #00000080",
    borderRadius: 2,
    padding: 2,
    zIndex: 6,
    top: -36,
    left: "calc(50% - 16px)",
    position: "absolute",
    "&:hover": {
      backgroundColor: "#788184",
    },
  },
  barImage: {
    width: "100%",
    position: "absolute",
    bottom: 0,
    zIndex: 3,
  },
  floorShelfBottom: {
    zIndex: 0,
  },
  imageSet: {
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    bottom: 0,
    zIndex: 0,
  },
  floorSecuringBar: {
    position: "absolute",
    bottom: ({ scaleFactor }) => 60 / scaleFactor,
    zIndex: -1,
  },
  weightsContainer: {
    fontSize: ({ scaleFactor }) => 112 / scaleFactor,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "bold",
    color: "#3f3f3f",
    display: "flex",
    position: "relative",
    zIndex: 111,
    height: ({ scaleFactor }) => 160 / scaleFactor,
    "& > div": {
      position: "absolute",
    },
  },
  //@todo these fixed values need to be calculated dynamically when scaling is added
  first: {
    fontWeight: "normal",
    left: ({ scaleFactor }) => 384 / scaleFactor,
  },
  second: {
    fontWeight: "normal",
    left: ({ scaleFactor }) => 1216 / scaleFactor,
  },
  third: {
    fontWeight: "normal",
    left: ({ scaleFactor }) => 2048 / scaleFactor,
  },
  fourth: {
    fontWeight: "normal",
    left: ({ scaleFactor }) => 2848 / scaleFactor,
  },
  boxWidthContainer: {
    position: "relative",
  },
  shelfBottom: {
    zIndex: 1,
  },
  boxWidth: {
    maxWidth: "100%",
    height: "100%",
    position: "absolute",
    bottom: ({ scaleFactor }) => -24 / scaleFactor,
  },
  floorSecuringMesh: {
    height: "calc(100% + 8px)",
    width: "100%",
    position: "absolute",
    top: ({ scaleFactor }) => -120 / scaleFactor, //top value updated to -15
  },
  securingMesh: {
    height: "100%",
    width: "100%",
    position: "absolute",
    top: ({ scaleFactor }) => -120 / scaleFactor, //top value updated to -15
    "&.top-shelf": {
      height: "calc(100% - 8px)",
      top: 0,
    },
  },
  securingBar: {
    position: "absolute",
    bottom: ({ scaleFactor }) => 224 / scaleFactor,
    zIndex: -1,
  },
  bottomSection: { position: "relative" },
  rowEditButton: {
    position: "absolute",
    left: "calc(50% - 12px)",
    bottom: 0,
    padding: 2,
    boxShadow: "0px 2px 4px #00000080",
    backgroundColor: "#c5da86",
    borderWidth: 1,
    borderColor: "#c5da86",
    borderRadius: 2,
    zIndex: 4,
    "&:hover": {
      backgroundColor: "#c5da86",
    },
  },
  rowAddButton: {
    position: "absolute",
    left: "calc(50% - 12px)",
    bottom: 0,
    padding: 2,
    backgroundColor: "#59A247",
    boxShadow: "0px 2px 4px #00000080",
    borderWidth: 1,
    borderColor: "#59A247",
    borderRadius: 2,
    zIndex: 4,
    "&:hover": {
      backgroundColor: "#59A247",
    },
  },
  leftBullBar: {
    position: "absolute",
    bottom: 0,
    left: ({ scaleFactor }) => -96 / scaleFactor,
    zIndex: 5,
  },
  rightBullBar: {
    position: "absolute",
    bottom: 0,
    right: ({ scaleFactor }) => -96 / scaleFactor,
    zIndex: 5,
  },
  centerBullBar: {
    position: "absolute",
    bottom: 0,
    zIndex: 5,
    left: 0,
  },
  supportBar: {
    position: "relative",
  },
  securityMesh: {
    position: "absolute",
    bottom: 19,
    zIndex: -5,
  },
  securityMesh100: {
    position: "absolute",
    bottom: 81.5,
    zIndex: -5,
  },
  securityMesh150: {
    position: "absolute",
    bottom: 144,
    zIndex: -5,
  },
  leftFrame: {
    position: "absolute",
    left: 0,
    zIndex: 0,
    bottom: 0,
  },
  rightFrame: {
    position: "absolute",
    right: 0,
    zIndex: 0,
    bottom: 0,
  },
  blocks: {
    position: "relative",
    display: "flex",
    height: "100%",
    alignItems: "center",
    justifyContent: "space-around",
  },
  block: {
    width: 80,
    margin: "0 5px",
    height: 60,
    backgroundColor: "orange",
    display: "flex",
    justifyContent: "center",
    fontWeight: "bold",
    color: "white",
    alignItems: "center",
    "&.small-block": {
      height: 30,
      color: "transparent",
    },
  },
  col: {
    display: "flex",
    flexDirection: "row",
  },
  shelfHeight: {
    left: ({ scaleFactor }) => 40 / scaleFactor,
    bottom: -54,
    minHeight: 54,
    position: "absolute",
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
  },
  shelfWidth: {
    bottom: "-80px",
    position: "absolute",
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    display: "flex",
  },
  arrow: {
    display: "flex",
    alignItems: "flex-start",
    width: "50%",
    flex: 1,
  },
}));

const FrontView2D = (data, { ...props }) => {
  const { t } = useTranslation(["frontView2d", "common"]);
  const dispatch = useDispatch();
  const optionsLoaded = useSelector((state) => {
    return state.Configuration.optionsLoaded;
  });
  const { artusComponentOptions, artusIdentifierAttributeValueMap } = useSelector((state) => {
    return state.Configuration;
  });
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const gridCellContainerRef = useRef();
  const id = get(data, "data.id", null);
  const heightIdentifier = get(data, "data.height", 2000);
  const height = Number(get(data, "data.height", 2000).match(/\d+/));
  const widthIdentifier = get(data, "data.width", 1900);
  const width = Number(get(data, "data.width", 1900).match(/\d+/));
  const supportbarIdentifier = get(data, "data.supportBars", 3);
  const supportBars = Number(get(data, "data.supportBars", 3).match(/\d+/));
  const shelfLoadIdentifier = get(data, "data.shelfLoad", 3850);
  const shelfLoad = Number(get(data, "data.shelfLoad", 3850).match(/\d+/));
  const shelfLoadForCalculation = Number(artusIdentifierAttributeValueMap.get(shelfLoadIdentifier)) || 2000;
  const nextFieldHeight = Number(get(data, "nextField.height", 2000).match(/\d+/));
  const fieldsLength = get(data, "data.length", 2);
  const scaleFactor = get(data, "scaleFactor", 6);
  const securing = get(data, "data.securing", "frontend_noSecuring");
  const layers = get(data, "data.layers", []);
  const leftBullBar = get(data, "data.leftBullBar", "no_bullbar");
  const rightBullBar = get(data, "data.rightBullBar", "no_bullbar");
  const isLeftOuterField = get(data, "data.isLeftOuterField", false);
  const isRightOuterField = get(data, "data.isRightOuterField", false);
  const weightExceedsMaxFieldLoad = get(data, "data.weightExceedsMaxFieldLoad", false);
  const palletWeight = get(data, "data.palletWeight");
  const weightExcessPopupShown = get(data, "data.weightExcessPopupShown");
  const scaledHeight = height / scaleFactor;
  const scaledWidth = width === 1900 ? width / scaleFactor + 8 : width / scaleFactor;
  const fieldHeight = Math.max(height, nextFieldHeight);
  const scaledFieldHeight = fieldHeight / scaleFactor;
  const [selectedLayer, setSelectedLayer] = useState(1);
  const [showFieldOptions, setShowFieldOptions] = useState(false);
  const [showLayerOptions, setShowLayerOptions] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ bottom: 0, left: 0 });
  const [cellHeight, setCellHeight] = useState(0);
  const [supportBarHeight, setSupportBarHeight] = useState(125);

  useEffect(() => {
    if (optionsLoaded) {
      const weightOptions = find(artusComponentOptions, { key: "weight" }, []);
      const selectedWeightOption = find(weightOptions.options, { identifier: shelfLoadIdentifier }, {});
      if (selectedWeightOption) {
        const heightAttributes = find(selectedWeightOption.attributes, { identifier: "height" }, []);
        const supportBarHeight = heightAttributes.values[0].value;
        setSupportBarHeight(Number(supportBarHeight));
      }
    }
  }, [optionsLoaded, shelfLoadIdentifier, setSupportBarHeight, artusComponentOptions]);

  const shelfHeight = Math.floor((height - 500 - supportBars * supportBarHeight) / supportBars / 25) * 25;

  const setWeightExcessPopupShown = () => {
    dispatch(updateField(id, "weightExcessPopupShown", true));
  };

  useEffect(() => {
    if (gridCellContainerRef && gridCellContainerRef.current) {
      setCellHeight(gridCellContainerRef.current.clientHeight);
    }
  }, [gridCellContainerRef]);

  const updateSupportBarsAmount = (amount) => {
    dispatch(updateField(id, "supportBars", amount));
  };

  const updateHeight = async (height) => {
    await dispatch(updateField(id, "height", height));
    return dispatch(updateOverallWidth());
  };

  const updateWidth = async (width) => {
    await dispatch(updateField(id, "width", width));
    return dispatch(updateOverallWidth());
  };

  const updateShelfLoad = (shelfLoad) => {
    dispatch(updateField(id, "shelfLoad", shelfLoad));
  };

  const updateLeftBullBar = (leftBullBar) => {
    dispatch(updateField(id, "leftBullBar", leftBullBar));
  };

  const updateRightBullBar = (rightBullBar) => {
    dispatch(updateField(id, "rightBullBar", rightBullBar));
  };

  const updateSecuring = (securing) => {
    dispatch(updateField(id, "securing", securing));
  };
  const updateLayerFloor = (floor) => {
    dispatch(updateLayer(id, selectedLayer, "floor", floor));
  };
  const removeCurrentField = () => {
    if (id !== 1 && id !== 2) {
      dispatch(removeField(id));
    }
  };

  const classes = useStyles({ scaleFactor });
  // original resolution * 2 to get real world values that we use
  //@todo we need this because we use topShelfHeight with the container div "supportBar", should be refactored to use gridTemplateRows
  const topShelfHeight = (500 + 230 - 54) / scaleFactor;
  const weightTextBottom = -(cellHeight - 10) / 2;
  const weightTextTopShelfBottom = -(topShelfHeight - 320 / scaleFactor) / 2 - 10;
  const bottomShelfHeight = (scaledHeight - topShelfHeight) / supportBars - 224 / scaleFactor;
  const middleShelvesHeight = bottomShelfHeight + 224 / scaleFactor;
  const palletPerShelf = width === 2700 ? 3 : width === 3600 ? 4 : 2;
  const weightPerPallet = weightExceedsMaxFieldLoad
    ? palletWeight
    : Math.floor(shelfLoadForCalculation / palletPerShelf / 5) * 5;
  const rowElements = [];
  for (let i = 0; i < supportBars; i++) {
    const layerShelfBottom = layers[i] ? layers[i].floor : "frontend_noBottom";
    const shelfBottomImage = layerShelfBottom.includes("grating")
      ? "grate"
      : layerShelfBottom.includes("noBottom")
      ? "nobottom"
      : layerShelfBottom.includes("chipboard")
      ? "chipboard"
      : "steel";
    rowElements.push(
      <div
        className={classes.supportBar}
        style={{
          minHeight: i === 0 ? topShelfHeight : middleShelvesHeight,
          maxHeight: i === 0 ? topShelfHeight : middleShelvesHeight,
        }}
        ref={gridCellContainerRef}
        key={`supportBar_${i}`}
      >
        <div
          className={clsx(classes.securingMesh, i === 0 && "top-shelf")}
          style={{
            backgroundImage:
              securing && securing.includes("Grating")
                ? `url(${baseUrl}/images/item/artus/image/frontview/securing_mesh_width_${width}.svg)`
                : "",
            backgroundSize: "contain",
          }}
        ></div>
        {securing && securing !== "frontend_noSecuring" && !securing.includes("Grating") && (
          <img
            className={classes.securingBar}
            style={{ maxWidth: scaledWidth }}
            src={`${baseUrl}/images/item/artus/image/frontview/securing_bar_width_${width}.svg`}
          />
        )}
        <div className={classes.imageSet}>
          <div className={classes.weightsContainer}>
            <div
              className={classes.first}
              style={{ bottom: i === 0 ? weightTextTopShelfBottom : weightTextBottom }}
            >
              {isNaN(weightPerPallet) ? "- - -" : weightPerPallet} kg
            </div>
            <div
              className={classes.second}
              style={{ bottom: i === 0 ? weightTextTopShelfBottom : weightTextBottom }}
            >
              {isNaN(weightPerPallet) ? "- - -" : weightPerPallet} kg
            </div>
            {(width === 2700 || width === 3600) && (
              <div
                className={classes.third}
                style={{ bottom: i === 0 ? weightTextTopShelfBottom : weightTextBottom }}
              >
                {isNaN(weightPerPallet) ? "- - -" : weightPerPallet} kg
              </div>
            )}
            {width === 3600 && (
              <div
                className={classes.fourth}
                style={{ bottom: i === 0 ? weightTextTopShelfBottom : weightTextBottom }}
              >
                {isNaN(weightPerPallet) ? "- - -" : weightPerPallet} kg
              </div>
            )}
          </div>

          <div
            className={classes.boxWidthContainer}
            style={{
              width: scaledWidth,
              height: i === 0 ? topShelfHeight - 320 / scaleFactor : cellHeight - 240 / scaleFactor,
            }}
          >
            <img
              className={classes.boxWidth}
              src={
                isSafari
                  ? `${baseUrl}/images/item/artus/image/frontview/box_width_${width}_safari.svg`
                  : `${baseUrl}/images/item/artus/image/frontview/box_width_${width}.svg`
              }
            />
          </div>
          <img
            className={classes.shelfBottom}
            style={{ maxWidth: scaledWidth }}
            src={`${baseUrl}/images/item/artus/image/frontview/shelfbottom_${shelfBottomImage}_width_${width}.svg`}
          />
        </div>
        <img
          src={`${baseUrl}/images/item/artus/image/frontview/supportbar_width_${width}_shelfLoad_${shelfLoad}.svg`}
          className={classes.barImage}
          style={{ maxWidth: scaledWidth }}
        />
        <div className={classes.layerEdit}>
          {optionsLoaded && (
            <IconButton
              className={
                layerShelfBottom === "frontend_noBottom" ? classes.rowAddButton : classes.rowEditButton
              }
              onClick={(e) => {
                setSelectedLayer(i + 1);
                setDialogPosition({
                  bottom: e.nativeEvent.clientY,
                  left: e.nativeEvent.clientX,
                });
                setShowLayerOptions(true);
              }}
            >
              {layerShelfBottom === "frontend_noBottom" ? (
                <AddIcon style={{ color: "#262626" }} />
              ) : (
                <EditIcon style={{ color: "#262626", transform: "scale(0.8)" }} />
              )}
            </IconButton>
          )}
        </div>
      </div>
    );
  }
  rowElements.push(
    <div className={classes.bottomSection} style={{ minHeight: bottomShelfHeight }}>
      <div
        className={classes.floorSecuringMesh}
        style={{
          backgroundImage:
            securing && securing.includes("full") && securing.includes("Grating")
              ? `url(${baseUrl}/images/item/artus/image/frontview/securing_mesh_width_${width}.svg)`
              : "",
          backgroundSize: "contain",
        }}
      ></div>
      {securing && securing.includes("full") && !securing.includes("Grating") && (
        <img
          className={classes.floorSecuringBar}
          style={{ maxWidth: scaledWidth }}
          src={`${baseUrl}/images/item/artus/image/frontview/securing_bar_width_${width}.svg`}
        />
      )}
      <div className={classes.imageSet}>
        <div className={classes.weightsContainer}>
          <div className={classes.first} style={{ bottom: weightTextBottom }}>
            {isNaN(weightPerPallet) ? "- - -" : weightPerPallet} kg
          </div>
          <div className={classes.second} style={{ bottom: weightTextBottom }}>
            {isNaN(weightPerPallet) ? "- - -" : weightPerPallet} kg
          </div>
          {(width === 2700 || width === 3600) && (
            <div className={classes.third} style={{ bottom: weightTextBottom }}>
              {isNaN(weightPerPallet) ? "- - -" : weightPerPallet} kg
            </div>
          )}
          {width === 3600 && (
            <div className={classes.fourth} style={{ bottom: weightTextBottom }}>
              {isNaN(weightPerPallet) ? "- - -" : weightPerPallet} kg
            </div>
          )}
        </div>
        <div
          className={classes.boxWidthContainer}
          style={{
            width: scaledWidth,
            height: cellHeight - 234 / scaleFactor,
          }}
        >
          <img
            id={"box-image"}
            className={classes.boxWidth}
            src={
              isSafari
                ? `${baseUrl}/images/item/artus/image/frontview/box_width_${width}_safari.svg`
                : `${baseUrl}/images/item/artus/image/frontview/box_width_${width}.svg`
            }
          />
        </div>
        <img
          className={classes.floorShelfBottom}
          style={{ width: scaledWidth }}
          src={`${baseUrl}/images/item/artus/image/frontview/floorpalette_width_${width}.svg`}
        />
      </div>
    </div>
  );
  return (
    <div className={classes.container}>
      <div className={clsx(classes.flexCol, id !== 1 && "spacing")}>
        {id === 1 && (
          <img
            src={`${baseUrl}/images/item/artus/image/frontview/frame_height_${height}.svg`}
            className={classes.leftFrame}
            style={{ height: scaledHeight }}
          />
        )}
        {id !== 1 && id !== 2 && (
          <IconButton className={classes.deleteButton} onClick={removeCurrentField}>
            <RemoveIcon style={{ color: "#262626" }} />
          </IconButton>
        )}
        <div
          className={classes.wrapper}
          style={{
            minHeight: scaledHeight,
            maxHeight: scaledHeight,
            minWidth: scaledWidth,
            width: scaledWidth,
            gridTemplateRows: `${topShelfHeight}px repeat(${supportBars - 1}, ${
              (scaledHeight - topShelfHeight) / supportBars - 1
            }px) ${(scaledHeight - topShelfHeight) / supportBars - 20}`, //@todo this calculation is not correct, need to refactor
          }}
        >
          {rowElements}
        </div>

        <img
          src={`${baseUrl}/images/item/artus/image/frontview/frame_height_${fieldHeight}.svg`}
          className={classes.rightFrame}
          style={{ height: scaledFieldHeight }}
        />
        {leftBullBar !== "no_bullbar" && (
          <img
            src={`${baseUrl}/images/item/artus/image/frontview/bullbar.svg`}
            style={{ height: `${450 / scaleFactor}px` }}
            className={id === 1 ? classes.leftBullBar : classes.centerBullBar}
          />
        )}
        {rightBullBar !== "no_bullbar" && id === fieldsLength && (
          <img
            src={`${baseUrl}/images/item/artus/image/frontview/bullbar.svg`}
            style={{ height: `${450 / scaleFactor}px` }}
            className={classes.rightBullBar}
          />
        )}
        <div className={classes.shelfHeight}>
          <div>{`${t("common:shelfHeight")}`}</div>
          <div>{`${shelfHeight} mm`}</div>
        </div>
        <div className={classes.shelfWidth} style={{ width: scaledWidth - 200 / scaleFactor }}>
          <div className={classes.arrow}>
            <img src={arrowUp} style={{ transform: "rotate(-90deg)" }} />
            <div style={{ width: "100%", height: 4.5, borderBottom: "1px solid #979797", marginLeft: -3 }} />
          </div>
          <div style={{ marginLeft: 20, marginRight: 20, minWidth: 54 }}>{`${width} mm`}</div>
          <div className={classes.arrow}>
            <div style={{ width: "100%", height: 4.5, borderBottom: "1px solid #979797", marginRight: -3 }} />
            <img src={arrowDown} style={{ transform: "rotate(-90deg)" }} />
          </div>
        </div>
        {optionsLoaded && (
          <IconButton
            className={classes.iconButton}
            onClick={(e) => {
              setDialogPosition({
                bottom: e.nativeEvent.clientY,
                left: e.nativeEvent.clientX,
              });
              setShowFieldOptions(true);
            }}
          >
            <EditIcon style={{ color: "#262626" }} />
          </IconButton>
        )}
        {showFieldOptions && (
          <FieldOptionsDialog
            show={true}
            onClose={() => setShowFieldOptions(false)}
            height={heightIdentifier}
            width={widthIdentifier}
            shelfLoad={shelfLoadIdentifier}
            supportBars={supportbarIdentifier}
            updateSupportBarsAmount={(val) => updateSupportBarsAmount(val)}
            updateHeight={(val) => updateHeight(val)}
            updateWidth={(val) => updateWidth(val)}
            updateShelfLoad={(val) => updateShelfLoad(val)}
            updateLeftBullBar={(val) => updateLeftBullBar(val)}
            updateRightBullBar={(val) => updateRightBullBar(val)}
            leftBullBar={leftBullBar}
            rightBullBar={rightBullBar}
            dialogPosition={dialogPosition}
            securing={securing}
            updateSecuring={(val) => updateSecuring(val)}
            isLeftOuterField={isLeftOuterField}
            isRightOuterField={isRightOuterField}
          />
        )}
        {showLayerOptions && (
          <LayerOptionsDialog
            show={true}
            updateFloor={(val) => updateLayerFloor(val)}
            onClose={() => setShowLayerOptions(false)}
            floor={layers[selectedLayer - 1].floor}
            dialogPosition={dialogPosition}
          />
        )}
        {!weightExcessPopupShown && weightExceedsMaxFieldLoad && !showFieldOptions && !showLayerOptions && (
          <AlertDialog
            dialogText={t("exceededWeight")}
            dialogTitle={t("hint")}
            confirmText={t("common:ok")}
            onClose={setWeightExcessPopupShown}
          />
        )}
      </div>
    </div>
  );
};
export default FrontView2D;
